import { useEffect, useState } from "react";
import { api } from "../api";
import type { AutomationSummary, FileStoreFile, Settings } from "../types";
import { DebugLog } from "./DebugLog";

interface Props {
  onClose: () => void;
  onChanged: () => void;
}

type Tab = "connection" | "generation" | "instructions" | "filestore" | "logs";

export function SettingsModal({ onClose, onChanged }: Props) {
  const [tab, setTab] = useState<Tab>("connection");
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  // Editable fields
  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [model, setModel] = useState("");
  const [automationId, setAutomationId] = useState("");
  const [interval, setInterval] = useState(24);
  const [webSearch, setWebSearch] = useState(true);
  const [webSearchTool, setWebSearchTool] = useState("tavily_web_search");
  const [fsBaseUrl, setFsBaseUrl] = useState("");
  const [fsToken, setFsToken] = useState("");

  // Instructions
  const [instructions, setInstructions] = useState("");
  const [instructionsLoaded, setInstructionsLoaded] = useState(false);

  // File store debug view
  const [fsFiles, setFsFiles] = useState<FileStoreFile[] | null>(null);
  const [fsError, setFsError] = useState<string | null>(null);
  const [fsSelected, setFsSelected] = useState<string | null>(null);
  const [fsContent, setFsContent] = useState<string | null>(null);
  const [fsContentLoading, setFsContentLoading] = useState(false);

  // Automations picker
  const [automations, setAutomations] = useState<AutomationSummary[] | null>(null);
  const [automationsError, setAutomationsError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const s = await api.getSettings();
        applySettings(s);
      } catch (err) {
        setBanner({ kind: "err", text: (err as Error).message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applySettings(s: Settings) {
    setSettings(s);
    setBaseUrl(s.northBaseUrl);
    setToken("");
    setModel(s.model);
    setAutomationId(s.automationId);
    setInterval(s.generationIntervalHours);
    setWebSearch(s.webSearchEnabled);
    setWebSearchTool(s.webSearchToolName);
    setFsBaseUrl(s.filestoreBaseUrl);
    setFsToken("");
  }

  async function saveConnection() {
    await withSave(async () => {
      const s = await api.saveSettings({
        northBaseUrl: baseUrl.trim(),
        // Only send tokens if the user typed a new one.
        ...(token ? { northApiToken: token } : {}),
        model: model.trim(),
        filestoreBaseUrl: fsBaseUrl.trim(),
        ...(fsToken ? { filestoreToken: fsToken } : {}),
      });
      applySettings(s);
      onChanged();
    }, "Connection saved.");
  }

  async function loadFilestore() {
    setFsError(null);
    setFsFiles(null);
    try {
      setFsFiles(await api.listFilestore());
    } catch (err) {
      setFsError((err as Error).message);
    }
  }

  async function openFsFile(name: string) {
    setFsSelected(name);
    setFsContent(null);
    setFsContentLoading(true);
    try {
      const r = await api.filestoreContent(name);
      setFsContent(r.content);
    } catch (err) {
      setFsContent(`⚠️ ${(err as Error).message}`);
    } finally {
      setFsContentLoading(false);
    }
  }

  async function deleteFsFile(name: string) {
    if (!window.confirm(`Delete "${name}" from the file store? This cannot be undone.`)) return;
    setFsError(null);
    try {
      await api.deleteFilestore(name);
      if (fsSelected === name) {
        setFsSelected(null);
        setFsContent(null);
      }
      await loadFilestore();
      onChanged(); // in case a brief was deleted, refresh the main list
    } catch (err) {
      setFsError((err as Error).message);
    }
  }

  async function saveGeneration() {
    await withSave(async () => {
      const s = await api.saveSettings({
        automationId: automationId.trim(),
        generationIntervalHours: Number(interval),
        webSearchEnabled: webSearch,
        webSearchToolName: webSearchTool.trim(),
      });
      applySettings(s);
      onChanged();
    }, "Generation settings saved.");
  }

  async function loadInstructions() {
    try {
      const r = await api.getInstructions();
      setInstructions(r.content);
      setInstructionsLoaded(true);
    } catch (err) {
      setBanner({ kind: "err", text: (err as Error).message });
    }
  }

  async function saveInstructions() {
    await withSave(async () => {
      await api.saveInstructions(instructions);
      onChanged();
    }, "Instructions saved. Takes effect on next generation.");
  }

  async function loadAutomations() {
    setAutomationsError(null);
    try {
      setAutomations(await api.listAutomations());
    } catch (err) {
      setAutomationsError((err as Error).message);
    }
  }

  async function generateNow() {
    await withSave(async () => {
      const r = await api.runNow();
      setBanner({ kind: r.ok ? "ok" : "err", text: r.message });
      onChanged();
      // Refresh run status into settings display.
      const s = await api.getSettings();
      setSettings(s);
    }, null);
  }

  async function withSave(fn: () => Promise<void>, okText: string | null) {
    setSaving(true);
    setBanner(null);
    try {
      await fn();
      if (okText) setBanner({ kind: "ok", text: okText });
    } catch (err) {
      setBanner({ kind: "err", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        <div className="tabs">
          <button className={tab === "connection" ? "active" : ""} onClick={() => setTab("connection")}>
            Connection
          </button>
          <button className={tab === "generation" ? "active" : ""} onClick={() => setTab("generation")}>
            Generation
          </button>
          <button
            className={tab === "instructions" ? "active" : ""}
            onClick={() => {
              setTab("instructions");
              if (!instructionsLoaded) void loadInstructions();
            }}
          >
            Instructions
          </button>
          <button
            className={tab === "filestore" ? "active" : ""}
            onClick={() => {
              setTab("filestore");
              void loadFilestore();
            }}
          >
            File Store
          </button>
          <button className={tab === "logs" ? "active" : ""} onClick={() => setTab("logs")}>
            Debug Log
          </button>
        </div>

        {banner && <div className={`banner ${banner.kind}`}>{banner.text}</div>}

        <div className="modal-body">
          {tab === "connection" && (
            <div className="form">
              <label>
                North base URL
                <input
                  type="text"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://your-instance.north.cohere.com/api"
                />
                <small>Full API root, including any /api prefix. Versioned paths (/v1/…) are appended.</small>
              </label>
              <label>
                North API token
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder={settings?.northApiTokenSet ? `saved (${settings.northApiToken})` : "paste token"}
                />
                <small>Stored server-side only. Leave blank to keep the current token.</small>
              </label>
              <label>
                Chat model (optional)
                <input type="text" value={model} onChange={(e) => setModel(e.target.value)} placeholder="North default" />
              </label>

              <hr className="divider" />
              <p className="section-note">
                <strong>File store</strong> — all file resources (briefs and the instructions file) live in the
                MCP file store, not North My Files.
              </p>
              <label>
                File store base URL
                <input
                  type="text"
                  value={fsBaseUrl}
                  onChange={(e) => setFsBaseUrl(e.target.value)}
                  placeholder="https://filestore.your-domain.com"
                />
                <small>Base host (no /files suffix). The app talks to its REST facade.</small>
              </label>
              <label>
                File store token
                <input
                  type="password"
                  value={fsToken}
                  onChange={(e) => setFsToken(e.target.value)}
                  placeholder={settings?.filestoreTokenSet ? `saved (${settings.filestoreToken})` : "paste bearer token"}
                />
                <small>Stored server-side only. Leave blank to keep the current token.</small>
              </label>

              <button className="primary" onClick={() => void saveConnection()} disabled={saving}>
                Save connection
              </button>
            </div>
          )}

          {tab === "generation" && (
            <div className="form">
              <details className="guide">
                <summary>📋 Automation setup guide — the contract</summary>
                <div className="guide-body">
                  <p>
                    Create one automation in the North UI (from the <strong>Daily Digest</strong> template),
                    publish it, then select it above. The app handles the rest. It just needs to honor this
                    contract:
                  </p>
                  <dl>
                    <dt>Prompt</dt>
                    <dd>
                      Keep it generic: <em>“Read the file <code>brief-instructions.md</code> and generate
                      today’s brief according to its instructions. Output the brief as Markdown.”</em> All
                      customization lives in the instructions file (see the Instructions tab), not the prompt.
                    </dd>

                    <dt>Instructions source</dt>
                    <dd>
                      The instructions file lives in the <strong>MCP file store</strong> (connect it there). On
                      each run:
                      <ul>
                        <li>
                          if the automation has a <strong>text</strong> input → the app reads
                          <code>brief-instructions.md</code> from the store and passes it <em>inline</em> (best);
                        </li>
                        <li>
                          else → the automation should read <code>brief-instructions.md</code> from the file
                          store MCP itself (via its <code>read_file</code> tool).
                        </li>
                      </ul>
                    </dd>

                    <dt>Output</dt>
                    <dd>
                      Return the finished brief as the automation’s <strong>final output</strong> (a Markdown
                      string) <em>or</em> as a single generated <strong>output file</strong>. The app takes that
                      output and writes it to the file store as <code>brief-YYYY-MM-DD.md</code>. (The automation
                      may also write there directly via the file store’s <code>write_file</code> tool.)
                    </dd>

                    <dt>Tools</dt>
                    <dd>
                      Enable web search (and any other tools) inside the automation if the brief should pull in
                      current information. The app polls the execution to completion and logs each step in the
                      Debug&nbsp;Log.
                    </dd>
                  </dl>
                </div>
              </details>
              <label>
                Automation id
                <input
                  type="text"
                  value={automationId}
                  onChange={(e) => setAutomationId(e.target.value)}
                  placeholder="id of the Daily Digest automation"
                />
              </label>
              <div className="automation-picker">
                <button className="link" onClick={() => void loadAutomations()} disabled={saving}>
                  Browse automations…
                </button>
                {automationsError && <div className="notice error">{automationsError}</div>}
                {automations && (
                  <ul className="automation-options">
                    {automations.length === 0 && <li className="muted">None found.</li>}
                    {automations.map((a) => (
                      <li key={a.id}>
                        <button className="link" onClick={() => setAutomationId(a.id)}>
                          <strong>{a.name}</strong>{" "}
                          <span className={`pub-badge ${a.publicationStatus}`} title="Draft automations cannot be executed — publish first.">
                            {a.publicationStatus}
                          </span>{" "}
                          <code>{a.id}</code>
                          {a.inputs.length > 0 && (
                            <span className="muted"> — inputs: {a.inputs.map((i) => `${i.name}:${i.type}`).join(", ")}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <label>
                Generation interval (hours)
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={interval}
                  onChange={(e) => setInterval(Number(e.target.value))}
                />
                <small>0 disables the cron (manual-only). Reschedules immediately on save.</small>
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={webSearch} onChange={(e) => setWebSearch(e.target.checked)} />
                Enable web search in chat
              </label>
              {webSearch && (
                <label>
                  Web search tool id
                  <input
                    type="text"
                    value={webSearchTool}
                    onChange={(e) => setWebSearchTool(e.target.value)}
                    placeholder="tavily_web_search"
                  />
                  <small>North hosted-tool id. If North rejects it, chat still answers (without web).</small>
                </label>
              )}
              <div className="row">
                <button className="primary" onClick={() => void saveGeneration()} disabled={saving}>
                  Save generation settings
                </button>
                <button className="secondary" onClick={() => void generateNow()} disabled={saving}>
                  Generate now
                </button>
              </div>
              {settings && (
                <div className="run-status">
                  <div>
                    <span className="muted">Last run:</span> {settings.lastRunAt ?? "never"}{" "}
                    {settings.lastRunStatus && <span className={`status-pill ${settings.lastRunStatus}`}>{settings.lastRunStatus}</span>}
                  </div>
                  <div>
                    <span className="muted">Next run:</span> {settings.nextRunAt ?? "not scheduled"}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "instructions" && (
            <div className="form">
              <label>
                brief-instructions.md
                <textarea
                  className="instructions-editor"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={16}
                  placeholder={"# Daily Brief Instructions\n\nSummarize…"}
                />
                <small>Stored in the file store as brief-instructions.md (overwritten on save). Applied on the next generation.</small>
              </label>
              <div className="row">
                <button className="secondary" onClick={() => void loadInstructions()} disabled={saving}>
                  Reload
                </button>
                <button className="primary" onClick={() => void saveInstructions()} disabled={saving}>
                  Save instructions
                </button>
              </div>
            </div>
          )}

          {tab === "filestore" && (
            <div className="filestore-view">
              <div className="filestore-toolbar">
                <span className="muted">Full contents of the MCP file store.</span>
                <button className="icon-btn" title="Refresh" onClick={() => void loadFilestore()}>
                  ⟳
                </button>
              </div>
              {fsError && <div className="notice error">{fsError}</div>}
              <div className="filestore-split">
                <div className="filestore-list scroll">
                  {!fsFiles && !fsError && <div className="notice muted">Loading…</div>}
                  {fsFiles && fsFiles.length === 0 && <div className="notice muted">Store is empty.</div>}
                  <ul>
                    {(fsFiles ?? []).map((f) => (
                      <li key={f.name} className="filestore-row">
                        <button
                          className={`filestore-item ${fsSelected === f.name ? "active" : ""}`}
                          onClick={() => void openFsFile(f.name)}
                        >
                          <span className="fs-name">{f.name}</span>
                          <span className="fs-meta">
                            {formatBytes(f.size)} · {f.content_type}
                          </span>
                          <span className="fs-meta">{new Date(f.modified_at).toLocaleString()}</span>
                        </button>
                        <button
                          className="fs-delete"
                          title={`Delete ${f.name}`}
                          aria-label={`Delete ${f.name}`}
                          onClick={() => void deleteFsFile(f.name)}
                        >
                          🗑
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="filestore-content scroll">
                  {!fsSelected && <div className="notice muted">Select a file to view its contents.</div>}
                  {fsSelected && (
                    <>
                      <div className="fs-content-header">{fsSelected}</div>
                      {fsContentLoading ? (
                        <div className="notice muted">Loading…</div>
                      ) : (
                        <pre className="fs-content-body">{fsContent}</pre>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "logs" && <DebugLog />}
        </div>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
